import { NgComponentOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'

import { TranslatePipe } from '@ngx-translate/core'
import { isEqual } from 'lodash-es'
import { PrimeTemplate } from 'primeng/api'
import { AutoComplete } from 'primeng/autocomplete'
import { BehaviorSubject } from 'rxjs'

import { DeviceService } from '@ti/core/common/services/device.service'

import { AutocompleteSheetComponent } from '../../../../inputs/autocomplete/autocomplete-sheet.component'

@Component({
  selector: 'app-autocomplete-select',
  templateUrl: 'autocomplete-select.component.html',
  styleUrl: 'autocomplete-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteSelectComponent),
      multi: true,
    },
  ],
  imports: [
    AutoComplete,
    AutocompleteSheetComponent,
    FormsModule,
    TranslatePipe,
    PrimeTemplate,
    NgComponentOutlet,
  ],
})
export class AutocompleteSelectComponent<V = unknown> implements ControlValueAccessor, OnChanges {
  @Output() readonly selectHide = new EventEmitter<void>()
  @Output() readonly selectShown = new EventEmitter<void>()
  @Output() readonly valueChange = new EventEmitter<V | null>()
  @Output() readonly completeMethod = new EventEmitter<any>()

  @Input() className = ''
  @Input() completeOnFocus = true
  @Input() delay = 300
  @Input() disabled = false
  @Input() forceSelection = true
  @Input() isCheckmarkVisible? = false

  @Input() optionLabel = 'title'
  @Input() optionValue: string | undefined = undefined
  @Input() placeholder = ''
  @Input() mobilePanelTitle = ''

  @Input() suggestions: any[] = []
  @Input() value?: V | null

  @ViewChild('itemSelect')
  protected readonly itemSelect?: AutoComplete

  @ViewChild(AutocompleteSheetComponent)
  protected readonly sheet?: AutocompleteSheetComponent

  protected readonly changeDetectorRef = inject(ChangeDetectorRef)
  protected readonly deviceService = inject(DeviceService)
  protected readonly filterDropdownShown$ = new BehaviorSubject<boolean>(false)

  protected onChange?: (value: any) => void
  protected onTouched?: () => void
  protected touched?: boolean
  protected _value?: any

  protected get filterBtnStyleClass(): string {
    return this.getFilterBtnClass(this.filterDropdownShown$.getValue(), this.disabled)
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (this.optionValue) {
      if (changes.value || changes.suggestions) {
      }
    } else {
      this._value = this.value
    }
  }

  public registerOnChange(onChange: (value: any) => void) {
    this.onChange = onChange
  }

  public registerOnTouched(onTouched: () => void) {
    this.onTouched = onTouched
  }

  // Reactive forms call these outside change detection
  public setDisabledState(disabled: boolean) {
    this.disabled = disabled
    this.changeDetectorRef.markForCheck()
  }

  public writeValue(value: V | null) {
    this.value = value

    // Use the whole option as a value internally to solve the bug in autocomplete
    if (this.optionValue) {
      this._value = this.suggestions.find((s) => s[this.optionValue!] === this.value)
    } else {
      this._value = this.value
    }
    this.changeDetectorRef.markForCheck()
  }

  protected getFilterBtnClass(dropdownStatus: boolean, disableModeStatus: boolean) {
    let filterButtonClass = dropdownStatus ? 'pi pi-chevron-up' : 'pi pi-chevron-down'
    filterButtonClass = disableModeStatus
      ? `${filterButtonClass} disabled`
      : `${filterButtonClass} clickable`
    return filterButtonClass
  }

  protected markAsTouched() {
    if (!this.touched) {
      this.onTouched?.()
      this.touched = true
    }
  }

  public openMobilePanel() {
    if (this.disabled) {
      return
    }
    this.sheet?.openMobilePanel('', this._value ?? undefined)
  }

  /** Phones use the bottom sheet; tablets and desktops keep the PrimeNG panel. */
  protected useMobileSheet(): boolean {
    return this.deviceService.isMobile()
  }

  protected onClickDropdown() {
    if (this.useMobileSheet()) {
      this.openMobilePanel()
      return
    }
    if (!this.itemSelect || this.disabled) {
      return
    }
    if (this.filterDropdownShown$.getValue()) {
      this.itemSelect.hide()
      return
    }
    this.itemSelect.onInputFocus({ target: { value: '' } })
  }

  protected onSelectHide() {
    if (this.filterDropdownShown$.getValue()) {
      this.filterDropdownShown$.next(false)
    }
    this.selectHide.emit()
  }

  protected onSelectShow() {
    if (!this.filterDropdownShown$.getValue()) {
      this.filterDropdownShown$.next(true)
    }
    this.selectShown.emit()
  }

  protected selectOption(item: any) {
    this.markAsTouched()
    const value = this.optionValue ? item.value?.[this.optionValue] : item.value
    this.writeValue(value)
    this.valueChange.emit(value)
    this.onChange?.(value)
  }

  protected selectMobileOption(option: any) {
    const value = this.optionValue ? option?.[this.optionValue] : option
    // Apply with the current option still marked is not a change
    if (!isEqual(value, this.value)) {
      this.selectOption({ value: option })
    }
  }

  protected unselectOption() {
    this.markAsTouched()
    this.writeValue(null)
    this.valueChange.emit(null)
    this.onChange?.(null)
  }

  protected clearOption() {
    this.writeValue(null)
    this.valueChange.emit(null)
    this.onChange?.(null)
  }

  protected onCompleteMethod(event: any) {
    this.completeMethod.emit(event)
  }

  protected onBlur() {
    // Opening the sheet blurs the field; its late reset would replace the sheet's results
    if (this.useMobileSheet()) {
      return
    }
    // Prevent options from disappearing before option click is registered
    setTimeout(() => {
      this.completeMethod.emit('')
    }, 256)
  }
}
